import React, { useState, useEffect, useRef } from "react";
import { Box, TextField, InputAdornment, Typography } from "@mui/material";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { formFieldStyles } from "../../../styles/formFieldStyles";
import { colors } from "../../../styles/colors";
import { borderRadius, floatingLabelMaskSx } from "../../../styles/theme";
import InfoTooltip from "../overlays/InfoTooltip";

type Variant = "side-label" | "floating";

interface InputFieldNumberProps {
  label?: string; // Optional: Label links vom Input-Feld (side-label) bzw. Floating-Label
  value: string;
  onChange?: (value: string) => void; // Wird bei jeder Eingabe aufgerufen (für Live-Updates)
  onBlur?: (value: string) => void; // Wird beim Verlassen des Feldes aufgerufen (für Speichern)
  placeholder?: string;
  readOnly?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  suffix?: string; // Optional: Text-Suffix (z.B. "kWh")
  error?: string;
  sx?: any;
  allowDecimals?: boolean; // Optional: Erlaube Dezimalzahlen (Standard: true)
  labelWidth?: number; // Optional: Feste Breite für das Label (nur bei variant='side-label')
  inputWidth?: number | string; // Optional: Breite für das Input-Feld (Default: flexibel)
  textAlign?: "left" | "right"; // Optional: Textausrichtung (Default: 'right')
  tooltip?: string; // Optional: Tooltip-Text bei Hover über das Label
  /**
   * Layout-Variante: 'side-label' (Standard, Label links neben dem Feld) oder 'floating'
   * (MUI Floating-Label wie bei InputField/Dropdown) – für Formulare, in denen Zahlenfelder
   * neben Text-/Auswahlfeldern derselben Bauform stehen sollen.
   */
  variant?: Variant;
  /**
   * Hintergrund hinter dem hochgewanderten Label bei variant='floating' – maskiert den
   * Umrandungsstrich, der sonst hinter dem Label durchscheint. Wie bei `InputField`.
   */
  labelBackgroundColor?: string;
}

/**
 * Hilfsfunktion: Konvertiert einen formatierten String zurück in eine parsebare Zahl
 * Wird für Rückwärtskompatibilität beibehalten, aber eigentlich nicht mehr nötig,
 * da NumericFormat bereits den rohen Wert liefert
 */
export const parseFormattedNumber = (value: string): string => {
  // Entferne Tausendertrennzeichen (Punkte) und ersetze Komma durch Punkt
  return value.replace(/\./g, "").replace(",", ".");
};

/**
 * Custom Input Component für NumericFormat
 * Wird als inputComponent für MUI's TextField verwendet
 */
interface CustomNumericFormatProps {
  inputRef: (instance: HTMLInputElement | null) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  allowDecimals?: boolean;
  inlineSuffix?: string; // Suffix direkt nach dem Wert (für linksbündige Darstellung)
}

const NumericFormatCustom = React.forwardRef<
  NumericFormatProps,
  CustomNumericFormatProps
>(function NumericFormatCustom(props, ref) {
  const {
    inputRef,
    onChange,
    allowDecimals = true,
    inlineSuffix,
    ...other
  } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      thousandSeparator="."
      decimalSeparator=","
      allowNegative={false}
      decimalScale={allowDecimals ? undefined : 0}
      fixedDecimalScale={false}
      suffix={inlineSuffix ? ` ${inlineSuffix}` : undefined}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
    />
  );
});

/**
 * Spezialisierte Komponente für numerische Eingaben mit automatischer Formatierung
 * Verwendet react-number-format für korrekte Formatierung
 * - Formatiert Zahlen während der Eingabe mit Tausendertrennzeichen (Punkte)
 * - Verwendet Komma als Dezimaltrennzeichen
 * - Keine Spinner-Buttons
 */
const InputFieldNumber: React.FC<InputFieldNumberProps> = ({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  readOnly = false,
  fullWidth = true,
  required = false,
  startIcon,
  endIcon,
  suffix,
  error,
  sx = {},
  allowDecimals = true,
  labelWidth = 200,
  inputWidth,
  textAlign = "right",
  tooltip,
  variant = "side-label",
  labelBackgroundColor = colors.background.paper,
}) => {
  const [internalValue, setInternalValue] = useState(value || "");
  // Ursprünglichen Wert merken für Restore bei leerem Blur
  const [originalValue, setOriginalValue] = useState(value || "");

  // Ref zum Input-Element für direkten DOM-Zugriff bei Blur
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setInternalValue(value || "");
    setOriginalValue(value || "");
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!readOnly) {
      const newValue = event.target.value;
      setInternalValue(newValue);
    }
  };

  // Bei Focus auf "0" das Feld leeren für einfachere Eingabe
  const [fokussiert, setFokussiert] = useState(false);

  const handleFocus = () => {
    setFokussiert(true);
    if (!readOnly && internalValue === "0") {
      setInternalValue("");
    }
  };

  const handleBlur = () => {
    setFokussiert(false);
    if (!readOnly) {
      // Lese den aktuellen Wert direkt aus dem DOM-Element
      // Das ist zuverlässiger als der State, da react-number-format's onValueChange
      // manchmal nach dem Blur-Event feuert (Race-Condition bei Tab-Wechsel)
      let currentValue = "";
      if (inputRef.current) {
        const formattedValue = inputRef.current.value || "";
        const valueWithoutSuffix = formattedValue.split(" ")[0]; // Entferne Inline-Suffix
        currentValue = parseFormattedNumber(valueWithoutSuffix);
      } else {
        currentValue = internalValue;
      }

      const finalValue = currentValue === "" ? originalValue : currentValue;
      setInternalValue(finalValue);

      // Priorität: onBlur, sonst onChange (für Abwärtskompatibilität)
      if (onBlur) {
        onBlur(finalValue);
      } else if (onChange) {
        onChange(finalValue);
      }
    }
  };

  // NumericFormat erwartet den rohen Wert (z.B. "5.25") und formatiert ihn selbst zur Anzeige
  const numericValue = internalValue;

  // Bei linksbündiger Ausrichtung: Suffix direkt nach dem Wert (inline)
  // Bei rechtsbündiger Ausrichtung: Suffix als InputAdornment am Feldende
  const useInlineSuffix = textAlign === "left" && suffix;

  const endAdornment =
    !useInlineSuffix && suffix ? (
      <InputAdornment position="end">
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {suffix}
        </Typography>
      </InputAdornment>
    ) : endIcon ? (
      <InputAdornment position="end">{endIcon}</InputAdornment>
    ) : undefined;

  const labelNode = tooltip ? (
    <InfoTooltip title={tooltip} placement="top">
      <span>{label}</span>
    </InfoTooltip>
  ) : (
    label
  );

  if (variant === "floating") {
    return (
      <Box sx={{ width: fullWidth ? "100%" : undefined }}>
        <TextField
          fullWidth={fullWidth}
          variant="outlined"
          label={labelNode}
          required={required}
          value={numericValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={readOnly ? undefined : placeholder}
          error={!!error}
          helperText={error}
          inputRef={inputRef}
          slotProps={{
            input: {
              readOnly: readOnly,
              inputComponent: NumericFormatCustom as any,
              startAdornment: startIcon ? (
                <InputAdornment position="start">{startIcon}</InputAdornment>
              ) : undefined,
              endAdornment: endAdornment,
            },
            htmlInput: {
              allowDecimals: allowDecimals,
              inlineSuffix: useInlineSuffix ? suffix : undefined,
            },
            // react-number-format verwaltet den Wert des <input> intern selbst – MUIs eigene
            // Erkennung, ob das Label hochgewandert bleiben soll (liest dafür normalerweise den
            // DOM-Wert des Inputs beim Verlassen des Feldes), trifft dabei nicht zuverlässig zu.
            // Deshalb explizit steuern statt MUI raten zu lassen – sonst rutscht das Label beim
            // Verlassen des Feldes zurück über den Wert.
            //
            // Der Fokus gehört mit in die Bedingung: Ohne ihn bliebe das Label in einem leeren
            // Feld sitzen und die ersten Ziffern liefen hinein.
            inputLabel: { shrink: numericValue !== "" || fokussiert },
          }}
          sx={{
            // Pill-Radius, Schatten, Innenabstand und Label-Ruheposition kommen bereits aus dem
            // Theme (formField). Hier bleibt nur, was vom Error-State und der Textausrichtung
            // abhängt und das Theme deshalb nicht pauschal setzen kann.
            "& .MuiOutlinedInput-root": {
              "&:hover fieldset": {
                borderColor: error
                  ? colors.status.error.main
                  : colors.border.focus,
              },
              "&.Mui-focused fieldset": {
                borderColor: error
                  ? colors.status.error.main
                  : colors.border.focus,
                borderWidth: 2,
              },
              "&.Mui-focused": {
                boxShadow: `0 4px 12px ${error ? colors.shadow.error : colors.shadow.focus}`,
              },
            },
            "& .MuiOutlinedInput-input": {
              textAlign,
            },
            ...floatingLabelMaskSx(labelBackgroundColor),
            ...sx,
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ ...formFieldStyles.container, mb: 0, ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Label links (optional, mit optionalem Tooltip) */}
        {label &&
          (tooltip ? (
            <InfoTooltip title={tooltip} placement="top">
              <Typography
                sx={{
                  width: labelWidth,
                  minWidth: labelWidth,
                  whiteSpace: "nowrap",
                  color: error ? colors.status.error.main : "text.primary",
                  fontWeight: required ? 500 : 400,
                }}
              >
                {required ? `${label} *` : label}
              </Typography>
            </InfoTooltip>
          ) : (
            <Typography
              sx={{
                width: labelWidth,
                minWidth: labelWidth,
                whiteSpace: "nowrap",
                color: error ? colors.status.error.main : "text.primary",
                fontWeight: required ? 500 : 400,
              }}
            >
              {required ? `${label} *` : label}
            </Typography>
          ))}

        {/* Input-Feld rechts */}
        <Box sx={{ flex: 1, width: inputWidth, maxWidth: inputWidth }}>
          <TextField
            fullWidth={fullWidth}
            variant="outlined"
            value={numericValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={readOnly ? undefined : placeholder}
            error={!!error}
            inputRef={inputRef}
            slotProps={{
              input: {
                readOnly: readOnly,
                inputComponent: NumericFormatCustom as any,
                startAdornment: startIcon ? (
                  <InputAdornment position="start">{startIcon}</InputAdornment>
                ) : undefined,
                endAdornment: endAdornment,
              },
              htmlInput: {
                allowDecimals: allowDecimals,
                inlineSuffix: useInlineSuffix ? suffix : undefined,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: readOnly ? 0 : `${borderRadius.field}px`,
                boxShadow: readOnly
                  ? "none"
                  : `0 2px 4px ${colors.shadow.light}`,
                transition: "box-shadow 0.2s ease-in-out",
                // Höhe: zentral aus `formField` (styles/theme.ts) – dort so berechnet, dass
                // sie zum Innenabstand passt und das Ruhe-Label mittig bleibt.
                backgroundColor: readOnly ? "transparent" : undefined,
                "&:hover": {
                  boxShadow: readOnly
                    ? "none"
                    : `0 4px 8px ${colors.shadow.medium}`,
                },
                "&:hover fieldset": {
                  borderColor: readOnly
                    ? "transparent"
                    : error
                      ? colors.status.error.main
                      : colors.border.focus,
                },
                "&.Mui-focused fieldset": {
                  borderColor: error
                    ? colors.status.error.main
                    : colors.border.focus,
                  borderWidth: 2,
                },
                "&.Mui-focused": {
                  boxShadow: error
                    ? `0 4px 12px ${colors.shadow.error}`
                    : `0 4px 12px ${colors.shadow.focus}`,
                },
                "& fieldset": {
                  borderRadius: readOnly ? 0 : `${borderRadius.field}px`,
                  border: readOnly ? "1px solid transparent" : undefined,
                },
              },
              // Innenabstand ebenfalls zentral aus `formField` – hier nur die Textausrichtung.
              "& .MuiOutlinedInput-input": {
                textAlign: textAlign,
              },
            }}
          />
        </Box>
      </Box>

      {error && (
        <Typography
          variant="caption"
          color="error"
          sx={{
            mt: 1,
            display: "block",
            fontSize: "0.75rem",
            fontWeight: 400,
            textAlign: "right",
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default InputFieldNumber;
